import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProvisioningService } from '../../services/provisioning/provisioning.service';
import { ToastService } from '../../services/toast/toast.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { CsvColumn, downloadCsv } from '../../common-files/csv';
import { downloadPdf } from '../../common-files/pdf';

// columns shared by the CSV and PDF exports
const CONFIRMED_COLUMNS: CsvColumn[] = [
  { key: 'bookingId', label: 'Booking' },
  { key: 'familyCode', label: 'Family code' },
  { key: 'guestName', label: 'Primary guest' },
  { key: 'category', label: 'Category' },
  { key: 'type', label: 'Type' },
  { key: 'mode', label: 'Mode' },
  { key: 'fromDate', label: 'From' },
  { key: 'toDate', label: 'To' },
  { key: 'guestCount', label: 'Guests' },
  { key: 'lifecycleStatus', label: 'Stage' },
];

const ALLOCATION_COLUMNS: CsvColumn[] = [
  { key: 'guestHouse', label: 'Guest house' },
  { key: 'room', label: 'Room / hall' },
  { key: 'capacity', label: 'Capacity' },
  { key: 'bookingId', label: 'Booking' },
  { key: 'familyCode', label: 'Family code' },
  { key: 'guestCount', label: 'Guests' },
  { key: 'fromDate', label: 'From' },
  { key: 'toDate', label: 'To' },
  { key: 'lifecycleStatus', label: 'Stage' },
];

@Component({
  selector: 'app-provision-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent],
  templateUrl: './provision-dashboard.component.html',
  styleUrl: './provision-dashboard.component.scss',
})
export class ProvisionDashboardComponent implements OnInit {
  openRows: any[] = [];
  stats: any = null;
  workingWindowOpen: number = 0;
  isLoading: boolean = false;

  // daily report exports
  reportDate: string = '';
  isExporting: boolean = false;

  constructor(
    private provisioningService: ProvisioningService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.reportDate = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.provisioningService.getDashboardCounts().subscribe({
      next: (res: any) => {
        const data = res?.data || {};
        this.openRows = data.openStatus?.listOfRequisitions || [];
        this.stats = data.checkinCheckout || null;
        this.workingWindowOpen = data.workingWindowOpen ?? 0;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load dashboard.');
      },
    });
  }

  exportConfirmed(format: 'csv' | 'pdf'): void {
    this.runExport(
      this.provisioningService.getDailyConfirmed(this.reportDate),
      CONFIRMED_COLUMNS,
      `confirmed-bookings-${this.reportDate}`,
      'Confirmed bookings',
      format
    );
  }

  exportAllocations(format: 'csv' | 'pdf'): void {
    this.runExport(
      this.provisioningService.getDailyAllocations(this.reportDate),
      ALLOCATION_COLUMNS,
      `allocations-${this.reportDate}`,
      'Room allocations',
      format
    );
  }

  private runExport(
    request: any,
    columns: CsvColumn[],
    fileName: string,
    title: string,
    format: 'csv' | 'pdf'
  ): void {
    if (!this.reportDate) {
      this.toastService.error('Pick a date first.');
      return;
    }
    this.isExporting = true;
    request.subscribe({
      next: async (res: any) => {
        const rows = res?.data?.rows || [];
        this.isExporting = false;
        if (!rows.length) {
          this.toastService.error('No rows for this date.');
          return;
        }
        if (format === 'csv') downloadCsv(fileName, columns, rows);
        else await downloadPdf(fileName, title, columns, rows, `Date ${this.reportDate}`);
      },
      error: (err: any) => {
        this.isExporting = false;
        this.toastService.error(err?.error?.message || 'Failed to build the report.');
      },
    });
  }
}
